import React, { ReactNode, ReactElement } from 'react'
import type { HeadProps } from 'gatsby'
import { CONTROL_D_DOMAIN } from 'gatsby-env-variables'
import { useLocation } from '@reach/router'
import { CONTROLDText } from 'ui/CONTROLD'

const siteBaseUrl = `https://${CONTROL_D_DOMAIN}/`

type SeoProps = {
  children?: ReactNode
  description?: string
  title?: string
  shouldAugmentTitle?: boolean
}

export function getDocumentHead({
  children,
  description = 'Control D is a free DNS service that improves your privacy and productivity. Block unwanted content, spoof your location, browse faster and monitor network level threats. No apps to install.',
  title,
  shouldAugmentTitle = true,
}: SeoProps): (headProps: HeadProps) => ReactElement {
  const augmentedTitle = shouldAugmentTitle ? `${title} | ${CONTROLDText}` : title

  return function () {
    const location = useLocation()

    /**
     * when we use <title>...</title> below, we are getting title generated by gatsby with the specific attribute,
     * but, in the dashboard, we set title using "document.title = title" (useUpdateDocumentTitle.ts). This leads to two titles appearing in the document,
     * which conflict with each other and as a result we get the wrong page title.
     * To avoid inconsistency we must set the title in the same way.
     */
    if (typeof window !== 'undefined' && title) {
      document.title = augmentedTitle || ''
    }

    return (
      <>
        <html lang="en" />
        <link
          rel="icon"
          type="image/png"
          href={
            // eslint-disable-next-line no-restricted-syntax
            location.origin === 'https://controld.dev'
              ? '/favicons/pikaman.png'
              : '/favicons/favicon_configured.ico'
          }
        />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        <meta property="twitter:image" content="https://controld.com/social/og_image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@ControlDNS" />
        <meta name="twitter:creator" content="@ControlDNS" />
        <meta name="twitter:label1" content="Platform" />
        <meta name="twitter:data1" content="Managed DNS" />
        <meta name="description" property="og:description" content={description} />
        <meta property="og:title" content={`${title ?? 'Control D'}`} />
        <meta property="og:site_name" content={`${title ?? 'Control D'}`} />
        <meta property="og:url" content={siteBaseUrl} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://controld.com/social/og_image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          name="google-site-verification"
          content="Wkde1h1CmQhtWrZXjkY3_R_oeu45iINZuoPVQJLhPHw"
        />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="Control D" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#000000" />
        {children}
      </>
    )
  }
}
